import React, { useState } from 'react'

import { css } from '@emotion/core'
import { Box, Container, Grid, Hidden } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import { Button, FormError, Header, Heading, Input, Main, Typography } from '~/components'
import { Theme } from '~/styles'
import { colors } from '~/styles/color'
import { useSetToken } from '~/utils'
import { useMutation } from '~/utils/useMutation'
import { renameSignupDraft } from '~/utils/signupDraft'
import api from '~/api'

import EMAIL_VERIFICATION_IMAGE from './../assets/img/EmailVerification.png'

const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 30_000

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const ChangeEmail: React.FC = () => {
  const history = useHistory()
  const { loginWithRedirect, user } = useAuth0()
  const setToken = useSetToken()
  const [newEmail, setNewEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const submitChangeEmail = useMutation<[], void>(async () => {
    setErrorMessage(null)
    if (!newEmail) {
      setErrorMessage('Please enter an email address.')
      return
    }
    try {
      await setToken.handler()
      const { jobId } = await api.rest.post<{ jobId: number }>('/bff/web/email-change/', {
        email: newEmail,
      })
      const startedAt = Date.now()
      while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
        await sleep(POLL_INTERVAL_MS)
        try {
          const { status } = await api.rest.get(`/bff/web/email-change/${jobId}/`)
          if (status === 202) continue
          // 200: Auth0 email updated (now unverified). Move the signup draft to
          // the new email so the flow resumes for it after re-login. The change
          // requires re-authentication, so send the user to the LOGIN screen with
          // the new email pre-filled — never the signup screen. On return they
          // land on the verification screen for the new address.
          if (user?.email) renameSignupDraft(user.email, newEmail)
          await loginWithRedirect({
            prompt: 'login',
            screen_hint: 'login',
            login_hint: newEmail,
            appState: { returnTo: '/emailverification' },
          })
          return
        } catch (err: any) {
          const status = err?.status
          if (status === 400) {
            setErrorMessage(
              'There was an issue updating your email. Please check the address and try again.',
            )
            return
          }
          throw err
        }
      }
      setErrorMessage("We couldn't update your email right now. Please try again in a moment.")
    } catch {
      setErrorMessage("We couldn't update your email right now. Please try again in a moment.")
    }
  })

  return (
    <>
      <Header />
      <Container>
        <Box mt={10}>
          <Grid container spacing={2}>
            <Hidden smDown>
              <Grid item md={6}>
                <Box mr={5} ml={-5}>
                  <img css={styles.image} src={EMAIL_VERIFICATION_IMAGE} alt="Email Verification" />
                </Box>
              </Grid>
            </Hidden>
            <Grid item container md={6} alignItems="center">
              <Main mx={3}>
                <Heading>Update your email address</Heading>
                <Typography variant="subText">
                  Enter a new email address below. We&apos;ll send a fresh verification link there.
                </Typography>
                <Box mt={4}>
                  <Input
                    label="New email"
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail((e.target as HTMLInputElement).value)}
                  />
                </Box>
                {errorMessage && (
                  <Box mt={2}>
                    <FormError>{errorMessage}</FormError>
                  </Box>
                )}
                <Box mt={3}>
                  <Button onClick={submitChangeEmail.handler} loading={submitChangeEmail.loading}>
                    Update email
                  </Button>
                </Box>
                <Box mt={2}>
                  <Button
                    buttonColor={colors.gray[100]}
                    textColor="#131D2B"
                    onClick={() => history.goBack()}
                  >
                    Back
                  </Button>
                </Box>
              </Main>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  image: (_theme: Theme) =>
    css({
      display: 'block',
      maxHeight: '50em',
      width: 'auto',
    }),
}

export default ChangeEmail
